import { FileQuestion, ServerCrash, ShieldAlert } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "./Button";

export function AccessDeniedPage({ moduleName }: { moduleName?: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center bg-page px-6 text-center py-12">
      <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 border border-rose-200 shadow-sm">
        <ShieldAlert className="h-10 w-10" />
      </div>
      <h1 className="mt-6 text-4xl font-bold tracking-tight text-ink">403 — Access Restricted</h1>
      <h2 className="mt-2 text-lg font-medium text-ink">Permission Required</h2>
      <p className="mt-2 max-w-md text-sm text-ink-secondary">
        Your current staff role does not have authorization to view {moduleName ? `the "${moduleName}" module` : "this module"}.
        Please contact your Super Administrator for access privileges.
      </p>
      <div className="mt-6 flex items-center gap-3">
        <Button variant="secondary" onClick={() => window.history.back()}>
          Go Back
        </Button>
        <Button onClick={() => navigate("/admin/dashboard")}>
          Return to Dashboard
        </Button>
      </div>
    </div>
  );
}

interface ErrorPageProps {
  code: number;
  title?: string;
  message?: string;
}

export function NotFoundPage() {
  return (
    <ErrorPage
      code={404}
      title="Page not found"
      message="The page you're looking for doesn't exist or may have been moved."
    />
  );
}

export function ServerErrorPage() {
  return (
    <ErrorPage
      code={500}
      title="Something went wrong"
      message="An unexpected error occurred on our servers. Please try again shortly."
    />
  );
}

function ErrorPage({ code, title, message }: ErrorPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const Icon = code === 404 ? FileQuestion : ServerCrash;

  const dashboardTarget = location.pathname.startsWith("/admin")
    ? "/admin/dashboard"
    : location.pathname.startsWith("/retailer")
    ? "/retailer/dashboard"
    : "/";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-page px-6 text-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary-50 text-primary-500">
        <Icon className="h-10 w-10" />
      </div>
      <h1 className="mt-6 text-[64px] font-bold leading-none text-ink">{code}</h1>
      <h2 className="mt-2 text-xl font-semibold text-ink">{title}</h2>
      <p className="mt-2 max-w-sm text-sm text-ink-secondary">{message}</p>
      <div className="mt-8">
        <Button onClick={() => navigate(dashboardTarget)} leftIcon={<span className="text-sm">←</span>}>
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
}

export default ErrorPage;